import { defineStore } from "pinia";
import { computed, ref, shallowRef, triggerRef } from "vue";
import pinia from "./index";
import { QuestionnaireType } from "../services/cms/cms-type-definitions";

export type SessionSection = {
  isDisplayed: boolean;
};

export const useCmsSessionStore = defineStore("cmsSessionStore", () => {
  const strengthMappingIndex = ref<number>(-1);
  const indicatorsMappingIndex = ref<number>(-1);

  const hasFinishedMapping = ref({
    strengths: false,
    indicators: false,
  });

  const hasFinishedTeamTagging = ref(false);

  // TODO: create sections at runtime rather than hardcoding
  const sections = shallowRef({
    section1: {
      title: "preStrengthsMapping",
      isDisplayed: true,
    },
    section2: {
      title: "strengthsMapping",
      isDisplayed: false,
    },
    section3: {
      title: "preIndicatorMapping",
      isDisplayed: false,
    },
    section4: {
      title: "indicatorMapping",
      isDisplayed: false,
    },
    section5: {
      title: "completedAllMappings",
      isDisplayed: false,
    },
  });

  const setStrengthMappingIndex = (index: number): void => {
    strengthMappingIndex.value = index;
  };

  const setIndicatorsMappingIndex = (index: number): void => {
    indicatorsMappingIndex.value = index;
  };

  const getSection = (index: number) => {
    const strengthIndex = strengthMappingIndex.value;
    const indicatorIndex = indicatorsMappingIndex.value;

    if (strengthIndex === -1) {
      return "section1";
    }

    if (index < strengthIndex) {
      return "section1";
    }

    if (index === strengthIndex) {
      return "section2";
    }

    if (index < indicatorIndex || indicatorIndex === -1) {
      return "section3";
    }

    if (index === indicatorIndex) {
      return "section4";
    }
    return "section5";
  };

  const sectionsToDisplay = computed(() => {
    return Object.keys(sections.value).filter((key) => sections.value[key].isDisplayed);
  });
  const updateSectionStatus = (section: string, isDisplayed: boolean): void => {
    const nextSectionToDisplay = `section${+section.slice(-1) + 1}`;
    const sectionNumber = Number(section.slice(-1));

    // Don't show sections beyond strength mapping if it's not completed!
    if (sectionNumber > 2 && hasFinishedMapping.value.strengths === false) return;

    // If user completes strength mapping and has already completed team tagging, skip to indicator rating
    if (sectionNumber === 2 && !!hasFinishedTeamTagging.value) {
      sections.value.section3.isDisplayed = isDisplayed;
      sections.value.section4.isDisplayed = isDisplayed;
    }

    sections.value[nextSectionToDisplay].isDisplayed = isDisplayed;
    triggerRef(sections);
  };

  const updateMappingStatus = (type: QuestionnaireType, hasFinished: boolean): void => {
    hasFinishedMapping.value[type] = hasFinished;
    triggerRef(hasFinishedMapping);
  };

  return {
    strengthMappingIndex,
    indicatorsMappingIndex,
    sections,
    setStrengthMappingIndex,
    setIndicatorsMappingIndex,
    getSection,
    sectionsToDisplay,
    updateSectionStatus,
    updateMappingStatus,
    hasFinishedTeamTagging,
    hasFinishedMapping,
  };
});

export default useCmsSessionStore(pinia());
